import React, { useState, FormEvent, ChangeEvent, JSX, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios'; // Import AxiosError for better error handling
import { LoadScript, GoogleMap, MarkerF } from '@react-google-maps/api'; // Import map components
import { Restaurant } from './types'; // Import the shared type
import StarRating from './StarRating'; // Import the new StarRating component
import './App.css'; // Import the updated CSS

const getBackendUrl = (): string => {
    const buildTimeBackendUrlPath = process.env.REACT_APP_BACKEND_URL || '/api';

    // If running in a browser, check for test environment
    if (typeof window !== 'undefined') {
        // If running in Cypress or test environment, use port 3001 for backend
        if (
            window.location.port === '3000' &&
            (process.env.NODE_ENV === 'test' || window.location.hostname === 'localhost')
        ) {
            const path = buildTimeBackendUrlPath.startsWith('/') ? buildTimeBackendUrlPath : `/${buildTimeBackendUrlPath}`;
            return `http://localhost:3001${path}`;
        }
        // Otherwise, use the current host (for production or dev)
        const path = buildTimeBackendUrlPath.startsWith('/') ? buildTimeBackendUrlPath : `/${buildTimeBackendUrlPath}`;
        return `${window.location.protocol}//${window.location.host}${path}`;
    }

    // Fallback for non-browser environments
    return `http://localhost:3001${buildTimeBackendUrlPath.startsWith('/') ? buildTimeBackendUrlPath : `/${buildTimeBackendUrlPath}`}`;
};
const BACKEND_URL_BASE: string = getBackendUrl(); // Renamed to BACKEND_URL_BASE

// --- Add Google Maps API Key from environment variables ---
const GOOGLE_MAPS_API_KEY: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

if (!GOOGLE_MAPS_API_KEY) {
    console.warn('Warning: REACT_APP_GOOGLE_MAPS_API_KEY environment variable is not set. Map functionality will be disabled.');
    // You could potentially disable map features or show a message if the key is missing
}

// Type for the API response which now includes the center coordinates
interface ApiResponse {
    restaurants: Restaurant[];
    center: { lat: number; lng: number };
}

// Type for the grouped structure
interface GroupedRestaurants {
    [category: string]: Restaurant[];
}

// Image cache to store successfully loaded images
const imageCache = new Map<string, boolean>();

// API response cache to store restaurant data
interface CacheEntry {
    data: ApiResponse;
    timestamp: number;
}

const apiCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Generate cache key from request data
const getCacheKey = (data: { 
    latitude?: number; 
    longitude?: number; 
    query?: string;
    filterOpenNow?: boolean;
    filterOffersTakeout?: boolean;
    restaurantType?: string; 
}): string => {
    const filterKeyPart = `open:${!!data.filterOpenNow}-takeout:${!!data.filterOffersTakeout}-type:${data.restaurantType || ''}`;
    if (data.latitude !== undefined && data.longitude !== undefined) {
        return `location:${data.latitude.toFixed(4)},${data.longitude.toFixed(4)}-${filterKeyPart}`;
    }
    return `query:${data.query || ''}-${filterKeyPart}`;
};

// Check if cache entry is still valid
const isCacheValid = (entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp < CACHE_DURATION;
};

// Component for lazy-loaded restaurant image with retry, exponential backoff, and caching
const RestaurantImage: React.FC<{ photoUrl: string; name: string; className: string }> = ({ photoUrl, name, className }) => {
    type ImageStatus = 'initial' | 'loading' | 'loaded' | 'retrying' | 'error'; // Error state kept for potential other error types

    const [status, setStatus] = useState<ImageStatus>('initial');
    const [retryCount, setRetryCount] = useState<number>(0);
    const [retryKey, setRetryKey] = useState<number>(0); // Used to force re-render of <img>

    // const maxRetries = 3; // Removed: Will retry indefinitely or until successful
    const baseDelay = 1000; // 1 second base delay, will increase exponentially

    // Check if image is already cached when component mounts or photoUrl changes
    React.useEffect(() => {
        if (imageCache.has(photoUrl)) {
            setStatus('loaded');
        } else {
            setStatus('loading'); // Start loading if not cached
        }
        setRetryCount(0);
        setRetryKey(0); // Reset key with photoUrl change
    }, [photoUrl]);

    const getRetryDelay = (attempt: number): number => {
        // Exponential backoff, e.g., 1s, 2s, 4s, 8s, 16s, 32s, etc.
        // Consider adding a cap to max delay if needed, e.g., Math.min(baseDelay * Math.pow(2, attempt), 60000) for a 1-minute max delay
        return baseDelay * Math.pow(2, attempt);
    };

    const handleImageLoad = () => {
        setStatus('loaded');
        imageCache.set(photoUrl, true);
        setRetryCount(0); // Reset retries on successful load
    };

    const handleImageError = () => {
        // Removed maxRetries check to allow indefinite retries
        setStatus('retrying');
        const delay = getRetryDelay(retryCount);
        
        setTimeout(() => {
            setRetryCount(prev => prev + 1);
            setRetryKey(prevKey => prevKey + 1); // Change key to trigger re-fetch
            setStatus('loading'); // Set back to loading for the new attempt
        }, delay);
        // The 'error' state will not be reached by exhausting retries anymore.
        // It's kept in ImageStatus for potential future use (e.g., unrecoverable network errors not related to retries).
    };
    
    // This error state would now typically only be reached if an initial, non-retryable error occurs,
    // or if handleImageError is modified in the future to set it under specific conditions other than retry exhaustion.
    if (status === 'error') {
        return (
            <div className="restaurant-photo-placeholder">
                <span>📷</span>
                <div className="retry-info">Image unavailable.</div>
            </div>
        );
    }

    if (status === 'retrying') {
        return (
            <div className="restaurant-photo-placeholder">
                <span>🔄</span>
                <div className="retry-info">Retrying attempt {retryCount + 1}...</div>
            </div>
        );
    }

    if (status === 'initial') { 
        return (
             <div className="restaurant-photo-placeholder">
                <span>⏳</span>
             </div>
        )
    }

    return (
        <img
            key={`${photoUrl}-${retryKey}`} 
            src={photoUrl} 
            alt={`${name}`}
            className={`${className} ${status === 'loaded' ? 'loaded' : 'loading'}`}
            loading="lazy" 
            onLoad={handleImageLoad}
            onError={handleImageError}
            referrerPolicy="no-referrer"
            style={{
                opacity: status === 'loaded' ? 1 : 0.7, 
                transition: 'opacity 0.3s ease-in-out'
            }}
        />
    );
};

function App(): JSX.Element { // Explicit return type for component
    const [query, setQuery] = useState<string>('');
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    // --- Add state for map center --- Defaults to a central location or null
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState<boolean>(false); // Track if a search has been performed
    const [filterOpenNow, setFilterOpenNow] = useState<boolean>(false); // State for Open Now filter
    const [filterOffersTakeout, setFilterOffersTakeout] = useState<boolean>(false); // State for Offers Takeout filter
    const [selectedRestaurantType, setSelectedRestaurantType] = useState<string>(''); // State for restaurant type

    // State to remember the last search parameters for re-fetching on filter change
    const [lastSearchQuery, setLastSearchQuery] = useState<string | null>(null);
    const [lastSearchLat, setLastSearchLat] = useState<number | null>(null);
    const [lastSearchLng, setLastSearchLng] = useState<number | null>(null);

    // Define restaurant type options for the dropdown
    const restaurantTypeOptions = [
        { value: "", label: "All Types" },
        { value: "bakery", label: "Bakery" },
        { value: "bar", label: "Bar" },
        { value: "cafe", label: "Cafe" },
        { value: "chinese_restaurant", label: "Chinese" },
        { value: "french_restaurant", label: "French" },
        { value: "indian_restaurant", label: "Indian" },
        { value: "italian_restaurant", label: "Italian" },
        { value: "japanese_restaurant", label: "Japanese" },
        { value: "korean_restaurant", label: "Korean" },
        { value: "mexican_restaurant", label: "Mexican" },
        { value: "pizza_restaurant", label: "Pizza" },
        { value: "seafood_restaurant", label: "Seafood" },
        { value: "steak_house", label: "Steak House" },
        { value: "sushi_restaurant", label: "Sushi" },
        { value: "thai_restaurant", label: "Thai" },
        { value: "vietnamese_restaurant", label: "Vietnamese" },
        // Add more types as needed
    ];

    // --- Function to Fetch Restaurants (Memoized with useCallback) ---
    const fetchRestaurants = useCallback(async (
        data: { 
            latitude?: number; 
            longitude?: number; 
            query?: string;
            filterOpenNow?: boolean;
            filterOffersTakeout?: boolean;
            restaurantType?: string; // Added restaurantType
        }
    ): Promise<void> => {
        setIsLoading(true);
        setError(null);
        // setRestaurants([]); // Don't clear restaurants immediately, allow showing stale while loading new
        setHasSearched(true); // Mark that a search attempt was made

        // Remember the search parameters that initiated this fetch
        if (data.query) {
            setLastSearchQuery(data.query);
            setLastSearchLat(null);
            setLastSearchLng(null);
        } else if (data.latitude !== undefined && data.longitude !== undefined) {
            setLastSearchQuery(null);
            setLastSearchLat(data.latitude);
            setLastSearchLng(data.longitude);
        }

        try {
            // Check cache first
            const cacheKey = getCacheKey(data);
            const cachedEntry = apiCache.get(cacheKey);
            
            if (cachedEntry && isCacheValid(cachedEntry)) {
                // Use cached data
                setRestaurants(cachedEntry.data.restaurants);
                setMapCenter(cachedEntry.data.center);
                setIsLoading(false);
                return;
            }

            // Make API request if no valid cache
            // Use BACKEND_URL_BASE and append the specific endpoint
            const response = await axios.post<ApiResponse>(`${BACKEND_URL_BASE}/restaurants`, data);
            
            // Cache the response
            apiCache.set(cacheKey, {
                data: response.data,
                timestamp: Date.now()
            });
            
            setRestaurants(response.data.restaurants);
            setMapCenter(response.data.center);
        } catch (err) {
            console.error("Error fetching restaurants:", err);
            let message = 'Failed to fetch restaurants. Please try again.';
            if (axios.isAxiosError(err)) {
                const errorResponse = err.response?.data as { message?: string };
                if (errorResponse?.message) {
                    message = errorResponse.message;
                } else if (err.message) {
                    message = err.message;
                }
            } else if (err instanceof Error) {
                message = err.message;
            }
            setError(message);
            setMapCenter(null); // Clear map center on error
        } finally {
            setIsLoading(false);
        }
    }, []);

    // --- Handlers ---
    const handleSearch = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!query.trim()) {
            setError('Please enter a location or use Current Location.');
            return;
        }
        const currentQuery = query.trim();
        // Pass query and current filter states to fetchRestaurants
        fetchRestaurants({ 
            query: currentQuery,
            filterOpenNow,
            filterOffersTakeout,
            restaurantType: selectedRestaurantType 
        });
        // Clear the search bar after submitting
        setQuery('');
    };

    const handleQueryChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setQuery(event.target.value);
        if (error) setError(null); // Clear error when user types
    }

    const handleUseCurrentLocation = (): void => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser.');
            return;
        }

        setIsLoading(true);
        setError(null);
        setRestaurants([]);
        setHasSearched(true); // Mark that a search attempt was made
        // setMapCenter(null); // Clear map center while loading new location

        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                const { latitude, longitude } = position.coords;
                // Pass latitude/longitude and current filter states to fetchRestaurants
                fetchRestaurants({ 
                    latitude, 
                    longitude,
                    filterOpenNow,
                    filterOffersTakeout,
                    restaurantType: selectedRestaurantType
                });
            },
            (geoError: GeolocationPositionError) => {
                console.error("Geolocation Error:", geoError);
                let message = 'Failed to get current location.';
                // ... (error handling messages remain the same)
                switch (geoError.code) {
                    case geoError.PERMISSION_DENIED:
                        message = 'Location permission denied. Please enable it in your browser settings.';
                        break;
                    case geoError.POSITION_UNAVAILABLE:
                        message = 'Location information is unavailable.';
                        break;
                    case geoError.TIMEOUT:
                        message = 'The request to get user location timed out.';
                        break;
                }
                setError(message);
                setIsLoading(false);
                setMapCenter(null); // Clear map on geolocation error
            },
            { timeout: 10000 }
        );
    };

    // --- Generate Google Maps Link ---
    const getGoogleMapsLink = (placeId: string, name: string, vicinity?: string): string => {
        const encodedName = encodeURIComponent(name);
        const encodedVicinity = vicinity ? encodeURIComponent(`, ${vicinity}`) : '';
        // Prioritize Place ID for accuracy
        if (placeId) {
            return `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${placeId}`;
        }
        // Fallback to name and vicinity (less reliable)
        return `https://www.google.com/maps/search/?api=1&query=${encodedName}${encodedVicinity}`;
    };

    // --- Process and Group Restaurants (Memoized) ---
    const groupedAndSortedRestaurants = useMemo((): GroupedRestaurants => {
        // Ensure sourceRestaurants is an array, default to empty array if not.
        const sourceRestaurants = Array.isArray(restaurants) ? restaurants : [];
        let filteredRestaurants = sourceRestaurants;

        // Apply "Open Now" filter if active
        if (filterOpenNow) {
            filteredRestaurants = filteredRestaurants.filter(resto => 
                resto.openNow === true || resto.openNow === undefined
                // Keep if explicitly open OR if status is unknown
            );
        }

        // Apply "Offers Takeout" filter if active
        if (filterOffersTakeout) {
            filteredRestaurants = filteredRestaurants.filter(resto => 
                resto.offersTakeout === true || resto.offersTakeout === undefined
                 // Keep if explicitly offers takeout OR if status is unknown (treat undefined as potentially offering takeout)
            );
        }

        // Proceed with grouping and sorting on the potentially filtered list
        if (filteredRestaurants.length === 0) {
            return {};
        }

        const grouped: GroupedRestaurants = filteredRestaurants.reduce((acc, resto) => {
            const category = resto.category || 'Uncategorized'; // Fallback category
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(resto);
            return acc;
        }, {} as GroupedRestaurants);

        // Sort within each category by Bayesian score (descending, N/A or undefined last)
        for (const category in grouped) {
            grouped[category].sort((a, b) => {
                const scoreA = typeof a.bayesianScore === 'number' ? a.bayesianScore : -1;
                const scoreB = typeof b.bayesianScore === 'number' ? b.bayesianScore : -1;
                if (scoreB !== scoreA) {
                    return scoreB - scoreA; // Primary sort: Descending Bayesian score
                }
                // Secondary sort (optional, if scores are equal): by name or original rating, etc.
                // For now, if scores are equal, maintain original order or sort by name for consistency
                return a.name.localeCompare(b.name);
            });
        }

        // Sort categories based on the highest Bayesian score within each category
        const sortedCategories = Object.keys(grouped).sort((a, b) => {
            const maxScoreA = grouped[a].length > 0 && typeof grouped[a][0].bayesianScore === 'number' ? grouped[a][0].bayesianScore : -1;
            const maxScoreB = grouped[b].length > 0 && typeof grouped[b][0].bayesianScore === 'number' ? grouped[b][0].bayesianScore : -1;
            
            if (maxScoreB !== maxScoreA) {
                return maxScoreB - maxScoreA; // Sort categories by highest score descending
            }
            return a.localeCompare(b); // Fallback to alphabetical if max scores are equal
        });

        const sortedGrouped: GroupedRestaurants = {};
        sortedCategories.forEach(category => {
            sortedGrouped[category] = grouped[category];
        });

        return sortedGrouped;
    }, [restaurants, filterOpenNow, filterOffersTakeout]);

    // --- Effect to re-fetch when filters change after an initial search ---
    useEffect(() => {
        // Only re-fetch if a search has been performed (mapCenter is a good indicator)
        // and it's not the initial load (isLoading is false)
        // and one of the search methods (query or lat/lng) is populated
        if (!isLoading && hasSearched && (lastSearchQuery || (lastSearchLat !== null && lastSearchLng !== null))) {
            const searchParams: Parameters<typeof fetchRestaurants>[0] = {
                filterOpenNow,
                filterOffersTakeout,
                restaurantType: selectedRestaurantType,
            };

            if (lastSearchQuery) {
                searchParams.query = lastSearchQuery;
            } else if (lastSearchLat !== null && lastSearchLng !== null) {
                searchParams.latitude = lastSearchLat;
                searchParams.longitude = lastSearchLng;
            }
            fetchRestaurants(searchParams);
        }
        // We want this effect to run when the filters change, OR when the last search parameters change
        // (e.g. user makes a new search, then changes a filter).
        // fetchRestaurants is memoized by useCallback and should not cause loops.
        // isLoading and hasSearched are guards.
    }, [selectedRestaurantType, filterOpenNow, filterOffersTakeout, lastSearchQuery, lastSearchLat, lastSearchLng, fetchRestaurants, isLoading, hasSearched]);

    // --- Map Configuration ---
    const mapContainerStyle = {
        width: '100%',
        height: '100%', // Let the container div control the height
    };

    const defaultCenter = { lat: 40.7128, lng: -74.0060 }; // Default center (e.g., NYC)

    const mapOptions = {
        disableDefaultUI: true, // Cleaner map
        zoomControl: true,
    };

    // Determine if results should be shown
    const showResults = !isLoading && hasSearched && Object.keys(groupedAndSortedRestaurants).length > 0;
    const showNoResultsMessage = !isLoading && hasSearched && !error && Object.keys(groupedAndSortedRestaurants).length === 0;
    const showInitialMessage = !isLoading && !hasSearched && !error;

    // --- Render ---
    return (
        <div className="App">
            <header className="app-header">
                <h1>🍜 NearEats</h1>
            </header>

            <main className="main-content">
                {/* Left Panel (Search, Map) - Becomes sticky on desktop */}
                <div className="left-panel">
                    <div className="search-controls">
                        <form onSubmit={handleSearch} className="search-form">
                            <input
                                type="text"
                                value={query}
                                onChange={handleQueryChange}
                                placeholder="Enter location (e.g., station, address)"
                                aria-label="Location search input"
                            />
                            <button type="submit" disabled={isLoading || !query.trim()}>
                                {isLoading ? 'Searching...' : 'Search'}
                            </button>
                        </form>
                        <button 
                            onClick={handleUseCurrentLocation} 
                            disabled={isLoading} 
                            className="current-location-btn"
                        >
                            Use Current Location
                        </button>

                        <div className="filter-section">
                            {/* --- Restaurant Type Filter --- */}
                            <div className="filter-dropdown-container">
                                <label htmlFor="restaurant-type-filter" className="filter-label">
                                    <span className="filter-icon">🍽️</span>
                                    Restaurant Type
                                </label>
                                <select
                                    id="restaurant-type-filter"
                                    value={selectedRestaurantType}
                                    onChange={(e) => setSelectedRestaurantType(e.target.value)}
                                    disabled={isLoading}
                                    className="type-filter-select"
                                >
                                    {restaurantTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* --- Open Now Filter --- */}
                            <div className="filter-toggle" onClick={() => !isLoading && setFilterOpenNow(!filterOpenNow)}>
                                <label htmlFor="open-now-filter">
                                    <span className="filter-icon">🕒</span>
                                    Only open restaurants
                                </label>
                                <input 
                                    type="checkbox" 
                                    id="open-now-filter"
                                    checked={filterOpenNow}
                                    onChange={(e) => setFilterOpenNow(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <div className="toggle-switch"></div>
                            </div>

                            {/* --- Offers Takeout Filter --- */}
                            <div className="filter-toggle" onClick={() => !isLoading && setFilterOffersTakeout(!filterOffersTakeout)}>
                                <label htmlFor="offers-takeout-filter">
                                    <span className="filter-icon">🥡</span>
                                    Only restaurants offering takeout
                                </label>
                                <input 
                                    type="checkbox" 
                                    id="offers-takeout-filter"
                                    checked={filterOffersTakeout}
                                    onChange={(e) => setFilterOffersTakeout(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <div className="toggle-switch"></div>
                            </div>
                        </div>
                    </div>

                    {/* Map Section */}
                    <div className="map-container">
                        {GOOGLE_MAPS_API_KEY ? (
                            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={mapCenter || defaultCenter}
                                    zoom={mapCenter ? 14 : 10}
                                    options={mapOptions}
                                >
                                    {restaurants?.map((resto) => (
                                        <MarkerF
                                            key={resto.id}
                                            position={resto.location}
                                            title={resto.name}
                                        // TODO: Add onClick handler for marker interaction?
                                        />
                                    ))}
                                    {/* Optional: Marker for the search center */}
                                    {/* {mapCenter && <MarkerF position={mapCenter} icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' }} />} */}
                                </GoogleMap>
                            </LoadScript>
                        ) : (
                            <p className="status-message warning">Map display requires a Google Maps API Key.</p>
                        )}
                    </div>
                </div>

                {/* Right Panel (Results) - Scrolls independently on desktop */}
                <div className="right-panel">
                    {/* Loading State */}
                    {isLoading && <p className="status-message loading" aria-live="polite">Finding restaurants...</p>}

                    {/* Error State */}
                    {error && <p className="status-message error" role="alert">{error}</p>}

                     {/* Initial Message */}
                     {showInitialMessage && (
                         <p className="status-message info-message">
                             Enter a location or use your current location to find nearby restaurants.
                         </p>
                     )}

                     {/* No Results Message */}
                     {showNoResultsMessage && (
                         <p className="status-message info-message">
                             No restaurants found matching your criteria. Try a different location.
                         </p>
                     )}

                    {/* Results Section */}
                    {showResults && (
                        <section className="results-section" aria-labelledby="results-heading">
                            {Object.entries(groupedAndSortedRestaurants).map(([category, restosInCategory]) => (
                                <div key={category} className="category-group">
                                    <h3 className="category-header">{category} ({restosInCategory.length})</h3>
                                    <ul className="restaurants-list">
                                        {restosInCategory.map((resto) => (
                                            <li key={resto.id} className="restaurant-card">
                                                <div 
                                                    className="restaurant-card-content"
                                                    onClick={() => window.open(getGoogleMapsLink(resto.placeId, resto.name, resto.vicinity), '_blank')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {resto.photoUrl && (
                                                        <div className="restaurant-photo-wrapper">
                                                            <RestaurantImage photoUrl={resto.photoUrl} name={resto.name} className="restaurant-photo" />
                                                            {typeof resto.bayesianScore === 'number' && (
                                                                <div className="score-badge">
                                                                    {Math.round(resto.bayesianScore)}%
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="restaurant-info">
                                                        <h4 className="restaurant-name">
                                                            {resto.name}
                                                            {/* Display Open/Closed Status Dot */}
                                                            {typeof resto.openNow === 'boolean' && (
                                                                <span className={`status-dot ${resto.openNow ? 'open' : 'closed'}`}></span>
                                                            )}
                                                        </h4>
                                                        <StarRating rating={resto.rating} />
                                                        {/* Display if openNow is undefined with a question mark */}
                                                        {resto.openNow === undefined && (
                                                            <span className="status-dot unknown" title="Open status unknown">?</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </section>
                    )}
                </div>
            </main>
        </div>
    );
}

export default App;